import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import PatientLanguageFields from "../../../components/PatientLanguageFields";
import OccupationSelect from "../../../components/OccupationSelect";
import DashboardLayout from "../../../layouts/DashboardLayout";
import axios from "axios";
import api from "../../../api";
import { Tooltip } from "../../../components/ui/Tooltip";

export default function PatientNew() {
  const navigate = useNavigate();
  const location = useLocation();
  const { patientId } = useParams(); // if present → edit mode

  // When arriving from "no patient found" on the appointment-booking page,
  // prefill the phone number that was searched and return there afterwards
  // (see AppointmentNew.js) instead of the default patient list.
  const prefillContact = location.state?.prefillContact || "";
  const returnTo = location.state?.returnTo || "";

  // Core patient form
  const [formData, setFormData] = useState({
    name: "",
    dob: "",
    gender: "",
    pronouns: "",
    contact: prefillContact,
    email: "",
    language: "en-US", // Default to English
    languages_known: [],
    address: "",
    consultationtype: "Online",
    patienttype: "Actual",
    emergencyname: "",
    emergencyrelation: "",
    emergencyphone: "",
    medications: "",
    conditions: "",
    allergies: "",
    therapyhistory: "",
    psychiatrichistory: "",
    familyhistory: "",
    substanceuse: "",
    concerns: "",
    duration: "",
    symptoms: "",
    occupation: "",
    relationshipstatus: "",
    supportsystem: "",
    lifestyle: "",
    consent: true,
  });

  // Custom persona state
  const [isCustomPersona, setIsCustomPersona] = useState(false);
  const [personaOptions, setPersonaOptions] = useState([]); // keys from actor_profiles.json
  const [customPersona, setCustomPersona] = useState({
    profileType: "",
    caseDescription: "",
    openness: "40", // Default to 40
    emotionLevel: "",
    exampleIssues: "",
    randomContext: "",
    speechPace: "",
    speechTone: "",
    speechHesitation: "",
    speechFillerWords: [],
    traumaEventSummary: "",
    traumaShamePoint: "",
    traumaFearPoint: "",
    cryingTriggers: "",
    autoThought: "",
    copingStrategies: "",
    intermediateBelief: "",
    worthlessBelief: "",
    unlovableBelief: "",
    behavior: "",
  });

  const fillerWordOptions = [
    "um",
    "uh",
    "you know",
    "like",
    "I guess",
    "maybe",
    "I don't know",
    "it's hard",
  ];

  const [emailError, setEmailError] = useState("");
  const [contactError, setContactError] = useState("");
  const [loading, setLoading] = useState(false);
  const [ageValue, setAgeValue] = useState("");

  // Load patient if editing
  useEffect(() => {
    if (patientId) {
      setLoading(true);
      (async () => {
        try {
          const res = await api.getPatient(id);
          const data = res.data;
          setFormData(data);
        } catch (err) {
          console.error("Error fetching patient", err);
          alert("Failed to load patient details.");
        } finally { setLoading(false) };
      }
      )
    }
  }, [patientId]);

  // Load persona options from backend (actor_profiles.json keys)
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const res = await api.getAllVirtualPatientProfiles();
        const data = res.data || {};
        const patients = data.patients || {};
        if (isMounted) {
          setPersonaOptions(Object.keys(patients));
        }
      } catch (err) {
        console.error("Error loading virtual patient profiles", err);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);


  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "email") {
      // Only validate if email is provided (not required)
      if (value.trim() === "") {
        setEmailError("");
      } else {
        const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
        setEmailError(isValid ? "" : "Please enter a valid email address.");
      }
    }

    if (name === "contact" || name === "emergencyphone") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
      if (name === "contact") {
        setContactError(
          digitsOnly.length > 0 && digitsOnly.length !== 10
            ? "Mobile number must be exactly 10 digits."
            : ""
        );
      }
      setFormData((prev) => ({ ...prev, [name]: digitsOnly }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCustomPersonaToggle = (e) => {
    const checked = e.target.checked;
    setIsCustomPersona(checked);
  };

  const handleCustomPersonaChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Filler words are handled separately
    setCustomPersona((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFillerWordToggle = (word) => {
    setCustomPersona((prev) => {
      const exists = prev.speechFillerWords.includes(word);
      return {
        ...prev,
        speechFillerWords: exists
          ? prev.speechFillerWords.filter((w) => w !== word)
          : [...prev.speechFillerWords, word],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (emailError || contactError) return;

    if (formData.contact && formData.contact.length !== 10) {
      alert("Mobile number must be exactly 10 digits.");
      return;
    }

    if (!formData.dob && !ageValue) {
      alert("Please enter either Date of Birth or Age.");
      return;
    }

    try {
      // Build payload
      const payload = { ...formData };

      // DOB takes priority over age when both are entered
      if (!payload.dob && ageValue) {
        payload.age = ageValue;
      }

      if (isCustomPersona && payload.patienttype === "Virtual") {
        payload.custom_persona = {
          enabled: true,
          profile_type: customPersona.profileType || null,
          case_description: customPersona.caseDescription || "",
          openness: customPersona.openness
            ? Number(customPersona.openness)
            : null,
          emotion_level: customPersona.emotionLevel
            ? Number(customPersona.emotionLevel)
            : null,
          example_issues: customPersona.exampleIssues || "",
          random_context: customPersona.randomContext || "",
          speech_pace: customPersona.speechPace || "",
          speech_tone: customPersona.speechTone || "",
          speech_hesitation: customPersona.speechHesitation || "",
          speech_filler_words: customPersona.speechFillerWords || [],
          trauma_event_summary: customPersona.traumaEventSummary || "",
          trauma_shame_point: customPersona.traumaShamePoint || "",
          trauma_fear_point: customPersona.traumaFearPoint || "",
          crying_triggers: customPersona.cryingTriggers
            ? customPersona.cryingTriggers.split("\n").filter((t) => t.trim())
            : [],
          auto_thought: customPersona.autoThought || "",
          coping_strategies: customPersona.copingStrategies || "",
          intermediate_belief: customPersona.intermediateBelief || "",
          worthless_belief: customPersona.worthlessBelief || "",
          unlovable_belief: customPersona.unlovableBelief || "",
          behavior: customPersona.behavior || "",
        };

        // Also send profile_key so backend can still validate base persona if needed
        if (customPersona.profileType) {
          payload.profile_key = customPersona.profileType;
        }
      }

      if (patientId) {
        // 🔹 Update patient
        const res = await api.updatePatient(id, payload);
        const data = res.data;
        alert("Patient updated successfully!");
        navigate("/patient"); // go back to patient list
      } else {
        // 🔹 Create new patient
        const res = await api.createPatient(payload);
        alert("Patient created successfully!");
        if (returnTo) {
          // Send the phone number back so the appointment page can re-search
          // and auto-select this patient instead of losing the ops person's place.
          navigate(returnTo, { state: { newPatientPhone: formData.contact } });
        } else {
          navigate("/patient"); // go back to patient list
        }
      }
    } catch (err) {
      console.error("Error saving patient", err);
      const errMsg = err.response?.data?.error || "Failed to save patient. Please try again.";
      alert(errMsg);
    }
  };

  if (loading) return <p className="text-center">Loading...</p>;

  return (
    <DashboardLayout>
      {/* Back Button */}
      <div className="mb-4 max-w-5xl mx-auto">
        <button
          type="button"
          onClick={() => navigate(returnTo || "/patient")}
          className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-800 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="font-medium">Back to Patients</span>
        </button>
      </div>
      <div className="max-w-5xl mx-auto bg-white rounded-2xl border border-secondary-100 shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-secondary-100 flex items-center gap-3">
          <div className="w-9 h-9 bg-primary-100 rounded-xl flex items-center justify-center">
            <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
          </div>
          <div>
            <h2 className="text-lg font-bold text-secondary-900">{patientId ? "Edit Patient" : "Patient Intake Form"}</h2>
            <p className="text-xs text-secondary-400">Patient information and medical history</p>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Personal Information */}
          <div>
            <h3 className="text-lg font-bold text-secondary-900 mb-4 border-b border-secondary-100 pb-2">Personal Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input name="name" onChange={handleChange} placeholder="Full Name" className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm" value={formData.name} required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                    Date Of Birth
                  </label>
                  <input type="date" name="dob" onChange={handleChange} className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm" value={formData.dob} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                    Age <span className="text-xs font-normal text-secondary-400">(if DOB unknown)</span>
                  </label>
                  <input
                    type="number"
                    name="age"
                    min="0"
                    max="120"
                    onWheel={(e) => e.target.blur()}
                    onChange={(e) => setAgeValue(e.target.value)}
                    placeholder="Age in years"
                    className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm"
                    value={ageValue}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                  Gender <span className="text-red-500">*</span>
                </label>
                <select name="gender" onChange={handleChange} className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm" value={formData.gender} required >
                  <option value="">Select Gender</option>
                  <option>Male</option>
                  <option>Female</option>
                </select>
                <input name="pronouns" list="pronoun-options" onChange={handleChange} value={formData.pronouns || ""} maxLength={40}
                  placeholder="Pronouns (optional), e.g. she/her"
                  className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm" />
                <datalist id="pronoun-options"><option value="she/her" /><option value="he/him" /><option value="they/them" /></datalist>
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="contact"
                  onChange={handleChange}
                  onWheel={(e) => e.target.blur()}
                  placeholder="10-digit mobile number"
                  className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm"
                  value={formData.contact}
                  inputMode="numeric"
                  maxLength={10}
                  pattern="\d{10}"
                  required
                />
                {contactError && (
                  <p className="text-red-500 text-sm mt-1">{contactError}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                  Email
                </label>
                <input type="email" name="email" onChange={handleChange} placeholder="Email" className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm" value={formData.email} />
                {emailError && (
                  <p className="text-red-500 text-sm mt-1">{emailError}</p>
                )}
              </div>
              <PatientLanguageFields language={formData.language} languagesKnown={formData.languages_known}
                onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))} />
              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                  Consultation Type <span className="text-red-500">*</span>
                </label>
                <select name="consultationtype" onChange={handleChange} className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm" value={formData.consultationtype} required >
                  <option value="">Consultation Type</option>
                  <option>Offline</option>
                  <option>Online</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                  Patient Type <span className="text-red-500">*</span>
                </label>
                <select name="patienttype" onChange={handleChange} className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm" value={formData.patienttype} required >
                  <option value="">Patient Type</option>
                  <option>Actual</option>
                  <option>Virtual</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                  Address
                </label>
                <textarea name="address" onChange={handleChange} placeholder="Address" className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm" value={formData.address} />
              </div>
            </div>
          </div>

          {/* Lifestyle (moved just below Personal Information) */}
          <div>
            <h3 className="text-lg font-bold text-secondary-900 mb-4 mt-8 border-b border-secondary-100 pb-2">Lifestyle</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                  Occupation
                </label>
                <OccupationSelect value={formData.occupation} onChange={(v) => setFormData((prev) => ({ ...prev, occupation: v }))} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                  Relationship Status <span className="text-red-500">*</span>
                </label>
                <select name="relationshipstatus" onChange={handleChange} className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm" value={formData.relationshipstatus} required >
                  <option value="">Relationship Status</option>
                  <option>Married</option>
                  <option>In Relationship</option>
                  <option>Single</option>
                  <option>Divorce</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                  Support System
                </label>
                <textarea name="supportsystem" onChange={handleChange} placeholder="Support System" className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm" value={formData.supportsystem} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                  Sleep, Diet, Exercise
                </label>
                <textarea name="lifestyle" onChange={handleChange} placeholder="Sleep, Diet, Exercise" className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm" value={formData.lifestyle} />
              </div>
            </div>

          </div>
          {/* Custom Persona Configuration (training patients only) */}
          {formData.patienttype === "Virtual" && (<div className="mt-6 border-t pt-4">
            <div className="flex items-center mb-2">
              <input
                type="checkbox"
                id="customPersona"
                className="mr-2"
                checked={isCustomPersona}
                onChange={handleCustomPersonaToggle}
              />
              <label htmlFor="customPersona" className="font-semibold">
                Custom Persona
              </label>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              Enable this to define a tailored virtual persona for this patient (description, openness, speech style, and trauma memory).
            </p>

            {isCustomPersona && (
              <div className="space-y-4 bg-gray-50 border rounded-lg p-4">
                {/* Profile Type */}
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                    Profile Type
                  </label>
                  <select
                    name="profileType"
                    value={customPersona.profileType}
                    onChange={handleCustomPersonaChange}
                    className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm"
                  >
                    <option value="">
                      Select profile
                    </option>
                    {personaOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Case Description */}
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                    Case Description
                  </label>
                  <textarea
                    name="caseDescription"
                    value={customPersona.caseDescription}
                    onChange={handleCustomPersonaChange}
                    className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm"
                    placeholder="Brief narrative of this specific patient (how they appear, their main struggle). Ex: Someone dealing with job loss who is overwhelmed by financial responsibilities and feeling hopeless about the future."
                  />
                </div>

                {/* Openness & Emotion Level */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1 flex items-center gap-2">
                      Openness (0-100)
                      <Tooltip content="Determines how willing the patient is to share information. Lower values (0-30) mean they are guarded/resistant. Higher values (70+) mean they are very open." position="right">
                        <span className="cursor-help text-gray-400 hover:text-blue-500">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
                        </span>
                      </Tooltip>
                    </label>
                    <input
                      type="number"
                      name="openness"
                      value={customPersona.openness}
                      onChange={handleCustomPersonaChange}
                      className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm"
                      min="0"
                      max="100"
                      placeholder="e.g. 30"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Hint: How open this patient can be (used for both min and max).
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 flex items-center gap-2">
                      Emotion Level (1-5)
                      <Tooltip content="Sets the baseline emotional intensity. 1 is flat/numb, 5 is highly volatile or intense." position="right">
                        <span className="cursor-help text-gray-400 hover:text-blue-500">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
                        </span>
                      </Tooltip>
                    </label>
                    <select
                      name="emotionLevel"
                      value={customPersona.emotionLevel}
                      onChange={handleCustomPersonaChange}
                      className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm"
                    >
                      <option value="">Select level</option>
                      <option value="1">1 - Very muted</option>
                      <option value="2">2 - Mildly emotional</option>
                      <option value="3">3 - Moderate intensity</option>
                      <option value="4">4 - Strong emotions</option>
                      <option value="5">5 - Very intense / labile</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Hint: Approximate baseline emotional intensity.
                    </p>
                  </div>
                </div>

                {/* Example Issues & Context */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                      Example Issues (Helpless Belief)
                    </label>
                    <textarea
                      name="exampleIssues"
                      value={customPersona.exampleIssues}
                      onChange={handleCustomPersonaChange}
                      className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm"
                      placeholder="e.g. nightmares, avoidance, guilt, anxiety... Ex: feeling like a failure"
                    />

                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                      Random Context (situation)
                    </label>
                    <textarea
                      name="randomContext"
                      value={customPersona.randomContext}
                      onChange={handleCustomPersonaChange}
                      className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm"
                      placeholder="Small background details that triggered the issue. Ex: recently had to tell kids they can't afford school supplies"
                    />
                  </div>
                </div>

                {/* Speech Style */}
                <div>
                  <h4 className="text-md font-bold text-secondary-800 mb-3 mt-6">Speech Style</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                        Pace
                      </label>
                      <select
                        name="speechPace"
                        value={customPersona.speechPace}
                        onChange={handleCustomPersonaChange}
                        className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm"
                      >
                        <option value="">Select pace</option>
                        <option value="slow">Slow</option>
                        <option value="medium">Medium</option>
                        <option value="fast">Fast</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Hint: Overall speaking speed.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                        Tone
                      </label>
                      <select
                        name="speechTone"
                        value={customPersona.speechTone}
                        onChange={handleCustomPersonaChange}
                        className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm"
                      >
                        <option value="">Select tone</option>
                        <option value="anxious">Anxious</option>
                        <option value="sad">Sad</option>
                        <option value="fearful">Fearful</option>
                        <option value="vulnerable">Vulnerable</option>
                        <option value="flat">Flat</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Hint: Emotional flavor of how they sound.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                        Hesitation
                      </label>
                      <select
                        name="speechHesitation"
                        value={customPersona.speechHesitation}
                        onChange={handleCustomPersonaChange}
                        className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm"
                      >
                        <option value="">Select hesitation</option>
                        <option value="none">None</option>
                        <option value="occasional">Occasional</option>
                        <option value="frequent">Frequent</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Hint: How often they pause or stumble.
                      </p>
                    </div>
                  </div>

                  {/* Filler words */}
                  <div className="mt-3">
                    <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                      Filler Words
                    </label>
                    <div className="flex flex-wrap gap-3">
                      {fillerWordOptions.map((word) => (
                        <label key={word} className="flex items-center text-sm">
                          <input
                            type="checkbox"
                            className="mr-1"
                            checked={customPersona.speechFillerWords.includes(
                              word
                            )}
                            onChange={() => handleFillerWordToggle(word)}
                          />
                          {word}
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Hint: Small phrases the patient tends to repeat.
                    </p>
                  </div>
                </div>
                {/* Cognitive Model Fields */}
                <div className="mt-6 border-t pt-4">
                  <h4 className="text-md font-bold text-secondary-800 mb-3 mt-6">Cognitive Model</h4>
                  <p className="text-xs text-gray-500 mb-3">
                    Cognitive-behavioral patterns that influence the patient's thoughts and responses.
                  </p>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                        Automatic Thought
                      </label>
                      <textarea
                        name="autoThought"
                        value={customPersona.autoThought}
                        onChange={handleCustomPersonaChange}
                        className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm"
                        rows={2}
                        placeholder="e.g. What if I run out of money? I should be able to do this on my own."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                        Coping Strategies
                      </label>
                      <textarea
                        name="copingStrategies"
                        value={customPersona.copingStrategies}
                        onChange={handleCustomPersonaChange}
                        className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm"
                        rows={2}
                        placeholder="e.g. Avoids asking for help and avoids challenges."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                        Intermediate Belief
                      </label>
                      <textarea
                        name="intermediateBelief"
                        value={customPersona.intermediateBelief}
                        onChange={handleCustomPersonaChange}
                        className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm"
                        rows={3}
                        placeholder="e.g. It's important to be responsible, competent, reliable and helpful. If I avoid challenges, I'll be okay, but if I try to do hard things I'll fail."
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                          Worthless Belief
                        </label>
                        <textarea
                          name="worthlessBelief"
                          value={customPersona.worthlessBelief}
                          onChange={handleCustomPersonaChange}
                          className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm"
                          rows={2}
                          placeholder="e.g. I am worthless, I don't matter..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                          Unlovable Belief
                        </label>
                        <textarea
                          name="unlovableBelief"
                          value={customPersona.unlovableBelief}
                          onChange={handleCustomPersonaChange}
                          className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm"
                          rows={2}
                          placeholder="e.g. I am unlovable, no one cares about me..."
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                        Behavior
                      </label>
                      <textarea
                        name="behavior"
                        value={customPersona.behavior}
                        onChange={handleCustomPersonaChange}
                        className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm"
                        rows={2}
                        placeholder="e.g. Continues to sit on couch; ruminates about failures. Avoids asking son for help."
                      />
                    </div>
                  </div>
                </div>
                {/* Trauma Memory */}
                <div>
                  <h4 className="text-md font-bold text-secondary-800 mb-3 mt-6">Trauma Memory (Optional)</h4>
                  <p className="text-xs text-gray-500 mb-2">
                    Hint: Fill this only if this case has a clear core trauma (PTSD, abuse, loss, etc.). Leave blank for non-trauma cases.
                  </p>
                  <div>
                    <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                      Event Summary
                    </label>
                    <textarea
                      name="traumaEventSummary"
                      value={customPersona.traumaEventSummary}
                      onChange={handleCustomPersonaChange}
                      className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm"
                      placeholder="One-line, non-graphic summary of what happened. Ex: She was assaulted by someone she once trusted, during a situation where she felt she couldn't escape."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                        Shame Point
                      </label>
                      <textarea
                        name="traumaShamePoint"
                        value={customPersona.traumaShamePoint}
                        onChange={handleCustomPersonaChange}
                        className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm"
                        placeholder="e.g. blames herself for not stopping it, feels it was her fault..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                        Fear Point
                      </label>
                      <textarea
                        name="traumaFearPoint"
                        value={customPersona.traumaFearPoint}
                        onChange={handleCustomPersonaChange}
                        className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm"
                        placeholder="e.g. fear of not being believed, fear it will happen again..."
                      />
                    </div>
                  </div>

                  <div className="mt-3">
                    <label className="block text-sm font-semibold text-secondary-700 mb-2 ml-1">
                      Crying Triggers
                    </label>
                    <textarea
                      name="cryingTriggers"
                      value={customPersona.cryingTriggers}
                      onChange={handleCustomPersonaChange}
                      className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm"
                      rows={3}
                      placeholder="Enter one trigger per line. Ex:&#10;Discussion of financial struggles&#10;Mention of children's needs not being met&#10;Being told they're not trying hard enough"
                    />
                  </div>
                </div>


              </div>
            )}
          </div>)}
          {/* Emergency Contact */}
          <div>
            <h3 className="text-lg font-bold text-secondary-900 mb-4 mt-8 border-b border-secondary-100 pb-2">Emergency Contact</h3>
            <div className="grid grid-cols-2 gap-4">
              <input name="emergencyname" onChange={handleChange} placeholder="Name" className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm" value={formData.emergencyname} />
              <input name="emergencyrelation" onChange={handleChange} placeholder="Relationship" className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm" value={formData.emergencyrelation} />
              <input type="tel" name="emergencyphone" onChange={handleChange} onWheel={(e) => e.target.blur()} placeholder="10-digit mobile number" className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm" value={formData.emergencyphone} inputMode="numeric" maxLength={10} pattern="\d{10}" />
            </div>
          </div>
          {/* Medical History */}
          <div>
            <h3 className="text-lg font-bold text-secondary-900 mb-4 mt-8 border-b border-secondary-100 pb-2">Medical History</h3>
            <div className="grid grid-cols-2 gap-4"> <textarea name="medications" onChange={handleChange} placeholder="Current Medications" className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm" value={formData.medications} /> <textarea name="conditions" onChange={handleChange} placeholder="Medical Conditions" className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm" value={formData.conditions} /> <textarea name="allergies" onChange={handleChange} placeholder="Allergies" className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm col-span-2" value={formData.allergies} /> </div>
          </div>
          {/* Psychological History */}
          <div>
            <h3 className="text-lg font-bold text-secondary-900 mb-4 mt-8 border-b border-secondary-100 pb-2">Psychological History</h3>
            <div className="grid grid-cols-2 gap-4"> <textarea name="therapyhistory" onChange={handleChange} placeholder="Previous Therapy" className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm" value={formData.therapyhistory} /> <textarea name="psychiatrichistory" onChange={handleChange} placeholder="Psychiatric Diagnoses" className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm" value={formData.psychiatrichistory} /> <textarea name="familyhistory" onChange={handleChange} placeholder="Family History of Mental Health" className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm" value={formData.familyhistory} /> <textarea name="substanceuse" onChange={handleChange} placeholder="Substance Use" className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm" value={formData.substanceuse} /> </div>
          </div>
          {/* Presenting Concerns */}
          <div>
            <h3 className="text-lg font-bold text-secondary-900 mb-4 mt-8 border-b border-secondary-100 pb-2">Presenting Concerns</h3>
            <div className="grid grid-cols-2 gap-4"> <textarea name="concerns" onChange={handleChange} placeholder="Reason for Therapy" className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm col-span-2" value={formData.concerns} /> <input name="duration" onChange={handleChange} placeholder="Duration of Issue" className="w-full px-4 py-3 border border-secondary-200 rounded-xl bg-secondary-50 text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 transition-all shadow-sm" value={formData.duration} /> </div>
          </div>
          {/* Consent */}
          <div className="flex items-center"> <input type="checkbox" name="consent" onChange={handleChange} className="w-5 h-5 mr-3 text-primary-600 border-secondary-300 rounded focus:ring-primary-500 bg-secondary-50" checked={formData.consent} /> <label> I consent to participate in therapy and understand confidentiality terms. </label> </div>
          <div className="flex justify-end mt-6 gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={() => navigate(returnTo || "/patient")}
              className="px-5 py-2.5 border border-secondary-200 text-secondary-600 rounded-lg text-sm font-semibold hover:bg-secondary-50 transition-colors"
            >
              Cancel
            </button>
            <button type="submit" disabled={!!emailError || !!contactError} className="px-5 py-2.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold transition-colors">
              Submit
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}